// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useCallback, useState } from 'react';

import {
  type TableView,
  type TableViewId,
  type TableViewType,
  type TableViewWithGridState,
  useEvolveGet,
} from '@evolvemep/api-ts';
import { Button, Divider, Flex, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { GridApi } from 'ag-grid-enterprise';

import { VariableTrackingModal } from 'components/Mantine/Modals/VariableTrackingModal';
import { CreateOrEditTableViewModal } from 'helpers/ag-grid/TableViewPanel/CreateOrEditTableViewModal';
import { loadSavedGridState } from 'helpers/ag-grid/TableViewPanel/loadSavedGridState';
import { TableViewOption } from 'helpers/ag-grid/TableViewPanel/TableViewOption';
import { useTableViews } from 'helpers/ag-grid/TableViewPanel/useTableViews';
import { stringifyGridStateJson } from 'helpers/ag-grid/TableViewPanel/utils';
import { isNotNil } from 'helpers/isNotNil';
import { setLocalStorageWrapped } from 'hooks/useLocalStorage';
import { useHasAccess } from 'hooks/user/useHasAccess';

type Props = {
  gridApi: GridApi;
  tableViewType: TableViewType;
};

export const TableViewPanel = ({ gridApi, tableViewType }: Props) => {
  const hasModifySavedViewsAccess = useHasAccess('savedViews', 'write');
  const [creating, setCreating] = useState(false);
  const [tableViewToRename, setTableViewToRename] = useState<TableView | null>(null);
  const [tableViewToDelete, setTableViewToDelete] = useState<TableView | null>(null);
  const createOrEditModalOpened = isNotNil(tableViewToRename) || creating;
  const {
    tableViews,
    loading,
    selectedTableViewId,
    setSelectedTableViewId,
    selectedTableViewModified,
    recordAppliedTableView,
    recordLatestSavedGridState,
    onTableViewDeleted,
    resetTableSessionState,
    createTableView,
    updateTableView,
    deleteTableView,
    mutationRunning,
    defaultTableViewId,
    onChangeDefaultTableViewId,
  } = useTableViews(tableViewType);

  const resetTableView = useCallback(() => {
    resetTableSessionState();
    gridApi.resetColumnState();
    gridApi.setFilterModel(null);
  }, [gridApi, resetTableSessionState]);

  const { apiCall: getTableViewApiCall, loading: loadingTableView } = useEvolveGet('admin/tableView/:tableViewId');

  const loadTableView = useCallback(
    ({ tableViewId, gridState }: TableViewWithGridState) => {
      recordAppliedTableView(tableViewId, gridState);
      loadSavedGridState(gridState, gridApi, true);
    },
    [gridApi, recordAppliedTableView],
  );

  const selectTableView = useCallback(
    async (tableViewId: TableViewId) => {
      setSelectedTableViewId(tableViewId);
      const tableView = await getTableViewApiCall({ url: `admin/tableView/${tableViewId}` });
      loadTableView(tableView);
    },
    [loadTableView, getTableViewApiCall, setSelectedTableViewId],
  );

  const handleChangeDefaultTableView = useCallback(
    async (updatedDefaultTableView: TableView | null) => {
      // Update the user preference for default table view ID.
      onChangeDefaultTableViewId(updatedDefaultTableView?.tableViewId ?? null);
      if (!updatedDefaultTableView) {
        // Clear local storage cache for default table view data (consumed by useInitialTableView when we initialize a table).
        setLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, null);
        return;
      }
      // Select and load the new default table view if it isn't already selected.
      const shouldToggleSelection =
        isNotNil(updatedDefaultTableView) && updatedDefaultTableView.tableViewId !== selectedTableViewId;
      // Optimistic UI update while we wait for the grid state to load.
      if (shouldToggleSelection) {
        setSelectedTableViewId(updatedDefaultTableView.tableViewId);
      }
      const tableView = await getTableViewApiCall({ url: `admin/tableView/${updatedDefaultTableView.tableViewId}` });
      // Update local storage cache for default table view data.
      setLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, tableView);
      if (shouldToggleSelection) {
        loadTableView(tableView);
      }
    },
    [
      onChangeDefaultTableViewId,
      selectedTableViewId,
      setSelectedTableViewId,
      getTableViewApiCall,
      loadTableView,
      tableViewType,
    ],
  );

  return (
    <>
      <Flex direction="column" h="100%" gap={2}>
        <Stack py="sm" px={8} flex="1 1 auto" gap={4} style={{ overflowY: 'auto' }}>
          {tableViews.length === 0 ? (
            <Text size="sm" c="gray" ta="center" px="sm">
              {loading ? <>Loading...</> : <>No table views have been created.</>}
            </Text>
          ) : (
            tableViews.map((tableView) => (
              <TableViewOption
                key={tableView.tableViewId}
                tableView={tableView}
                checked={selectedTableViewId === tableView.tableViewId}
                modified={selectedTableViewId === tableView.tableViewId && selectedTableViewModified}
                loading={selectedTableViewId === tableView.tableViewId && loadingTableView}
                isDefault={defaultTableViewId === tableView.tableViewId}
                onChangeDefaultTableView={handleChangeDefaultTableView}
                onClick={() => selectTableView(tableView.tableViewId)}
                onDelete={() => setTableViewToDelete(tableView)}
                onRename={() => setTableViewToRename(tableView)}
                onSave={() => {
                  const gridState = stringifyGridStateJson(gridApi.getState());
                  void updateTableView(tableView.tableViewId, {
                    gridState,
                  }).then((updated) => recordLatestSavedGridState(updated.tableViewId, updated.gridState));
                  if (defaultTableViewId === tableView.tableViewId) {
                    // Update local storage cache for default table view data.
                    setLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, {
                      ...tableView,
                      gridState,
                    });
                  }
                }}
                canSaveViews={hasModifySavedViewsAccess}
              />
            ))
          )}
        </Stack>
        <Flex direction="column" bg="gray.0">
          <Divider w="100%" />
          <Stack p="xs" gap="xs">
            {hasModifySavedViewsAccess && (
              <Button
                variant="light"
                fullWidth
                onClick={() => setCreating(true)}
                disabled={createOrEditModalOpened || mutationRunning}
              >
                Create table view
              </Button>
            )}
            <Button variant="default" fullWidth onClick={resetTableView}>
              Reset
            </Button>
          </Stack>
        </Flex>
      </Flex>
      <CreateOrEditTableViewModal
        opened={createOrEditModalOpened}
        onClose={() => {
          setTableViewToRename(null);
          setCreating(false);
        }}
        tableView={tableViewToRename}
        gridApi={gridApi}
        createTableView={createTableView}
        onTableViewCreated={(createdTableView) => {
          notifications.show({
            title: 'Successfully created',
            message: `Table view ${createdTableView.name} created.`,
            color: 'green',
          });
          recordAppliedTableView(createdTableView.tableViewId, createdTableView.gridState);
        }}
        updateTableView={updateTableView}
        onTableViewUpdated={(updatedTableView) => {
          notifications.show({
            title: 'Successfully renamed',
            message: `Table view renamed to ${updatedTableView.name}.`,
            color: 'green',
          });
        }}
      />
      <VariableTrackingModal
        variable={tableViewToDelete}
        title="Delete table view"
        size="sm"
        buttonColor="red"
        confirmationText="Delete"
        onClose={() => setTableViewToDelete(null)}
        onConfirm={async (tableView) => {
          await deleteTableView(tableView.tableViewId).then(() => {
            notifications.show({
              title: 'Successfully deleted',
              message: `Table view "${tableView.name}" deleted.`,
              color: 'green',
            });
            onTableViewDeleted(tableView.tableViewId);
          });
        }}
      >
        {(tableView) => (
          <Text>
            Are you sure you want to delete table view <b>{tableView.name}</b>?
          </Text>
        )}
      </VariableTrackingModal>
    </>
  );
};
